import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Navigation, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';

import { SYMBOL } from '../../images';
import { BACK_ICON, LEFT_ICON, RIGHT_ICON, STREAMING_ICON } from '../../icons';
import { BREAK_POINTS } from '../../constants';

const Wrapper = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  display: grid;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    gap: 120px;
  }

  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    gap: 80px;
  }
`;

function ArtistDetailPresenter({ artist, works, menuList, onClickBack, onClickLink }) {
  const NO_WORKS = Object.keys(works).length === 0;
  return (
    <Wrapper>
      <Profile artist={artist} onClickBack={onClickBack} />
      {NO_WORKS ? <></> : <Works works={works} menuList={menuList} onClickLink={onClickLink} />}
    </Wrapper>
  );
};

const MobileButtonWrapper = styled.div`
  grid-area: button;
  height: 64px;
  padding: 0 16px;
  display: none;
  align-items: center;
  justify-content: space-between;

  img {
    width: 32px;
    height: 32px;
    cursor: pointer;
  }

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    display: flex;
  }
`;

const MobileButton = ({ swiperRef, onClickBack }) => {
  return (
    <MobileButtonWrapper>
      <img src={BACK_ICON} alt="Back" onClick={onClickBack} />
      <div>
        <img src={LEFT_ICON} alt="Previous" onClick={() => swiperRef.current?.slidePrev()} />
        <img src={RIGHT_ICON} alt="Next" onClick={() => swiperRef.current?.slideNext()} />
      </div>
    </MobileButtonWrapper>
  );
};

const ProfileSection = styled.section`
  display: grid;
  grid-template-columns: 420px 760px;
  grid-template-areas: 'text image';
  height: 840px;
  margin: 40px 0 160px;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    grid-template-columns: 1fr;
    grid-template-areas: 
      'button'
      'image'
      'text';
    height: auto;
    margin: 0;
  }
`;

const Profile = ({ artist, onClickBack }) => {
  const swiperRef = React.useRef(null);

  return (
    <ProfileSection>
      <MobileButton swiperRef={swiperRef} onClickBack={onClickBack} />
      <ProfileText artist={artist} onClickBack={onClickBack} />
      <ProfileImage swiperRef={swiperRef} artist={artist.profile.images} />
    </ProfileSection>
  );
};

const ProfileTextWrapper = styled.div`
  grid-area: text;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  color: #505050;
  font-family: NanumSquareNeo;

  .profile-name { margin: 0 0 40px; }
  .profile-name-kor {
    font-size: 48px;
    line-height: 1.04;
    letter-spacing: -0.91px;
  }
  .profile-name-eng {
    margin: 20px 0 0;
    font-size: 24px;
    line-height: 1.25;
    letter-spacing: -0.83px;
  }

  .profile-date { margin: 80px 0; }
  .profile-date-item { 
    margin: 20px 0;
    display: flex;
    justify-content: flex-start;
  }
  .profile-label {
    flex-shrink: 0;
    margin: 0 40px 0 0;
    font-weight: bold;
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.38px;
  }
  .profile-value {
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.38px;
  }

  .profile-desc {
    display: flex;
    flex-direction: column;
  }
  .profile-desc-label {
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.38px;
    color: #969696;
  }
  .profile-desc-value {
    font-size: 20px;
    line-height: 2;
    letter-spacing: -0.5px;
    color: #969696;
  }

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    padding: 0 24px;

    .profile-name { margin: 40px 0 0 0; }
    .profile-name-kor { font-size: 36px; }
    .profile-name-eng { font-size: 30px; }

    .profile-date {  }
    .profile-date-item {  }
    .profile-label { font-size: 18px; }
    .profile-value { font-size: 18px; }

    .profile-desc {  }
    .profile-desc-label { font-size: 18px; }
    .profile-desc-value { font-size: 18px; }
  }
  
  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    .profile-name { margin: 40px 0 0 0; }
    .profile-name-kor { font-size: 24px; }
    .profile-name-eng { font-size: 20px; }

    .profile-date {  }
    .profile-date-item {  }
    .profile-label { font-size: 12px; }
    .profile-value { font-size: 12px; }

    .profile-desc {  }
    .profile-desc-label { font-size: 12px; }
    .profile-desc-value { font-size: 12px; }
  }
`;

const BackButton = styled.img`
  width: 32px;
  height: 32px;
  cursor: pointer;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    display: none;
  }
`;

const ProfileText = ({ artist, onClickBack }) => {

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('.');
    const formattedMonth = parseInt(month);
    const formattedDay = parseInt(day);
    return `${year}년 ${formattedMonth}월${formattedDay}일`;
  };

  return (
    <ProfileTextWrapper>
      <BackButton src={BACK_ICON} alt="Back" onClick={onClickBack} />

      <div>
        <div className="profile-name">
          <div className="profile-name-kor">{artist.nameKor}</div>
          <div className="profile-name-eng">{artist.nameEng}</div>
        </div>

        <div className="profile-date">
          <div className="profile-date-item">
            <span className="profile-label">출생</span>
            <span className="profile-value">{formatDate(artist.bio.birth)}</span>
          </div>
          <div className="profile-date-item">
            <span className="profile-label">데뷔</span>
            <span className="profile-value">{artist.bio.debut}</span>
          </div>
        </div>

        {artist.bio.awards.length !== 0 && (
          <>
            <Title style={{ margin: '0 0 20px' }}>AWARDS</Title>
            <div className='profile-desc'>
              {artist.bio.awards.map((award, index) => (
                <span className="profile-desc-value" key={index}>{award}</span>
              ))}
            </div>
          </>
        )}
      </div>
    </ProfileTextWrapper>
  );
};

const ProfileImageWrapper = styled.div`
  grid-area: image;
  width: 100%;
  position: relative;

  .swiper {
    width: calc(100% - 100px);
    height: 840px;
    margin: 0 auto;
  }

  .swiper-slide {
    width: 100% !important;
    aspect-ratio: 3/4;
  }

  img.swiper-slide-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    .swiper {
      width: 100%;
      height: auto;
    }

    .swiper-slide {
      aspect-ratio: 3/4;
      width: 100% !important;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    img.swiper-slide-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      max-height: 100vh;
    }

    .prev-button, .next-button {
      display: none;
    }
  }
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;

  &.prev-button {
    left: 0;
  }

  &.next-button {
    right: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ProfileImage = ({ artist, swiperRef }) => {
  const navigationPrevRef = React.useRef(null);
  const navigationNextRef = React.useRef(null);

  const swiperSettings = {
    modules: [Navigation, Autoplay],
    spaceBetween: 30,
    slidesPerView: 1,
    loop: true,
    speed: 500,
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
    },
    onSwiper: (swiper) => swiperRef.current = swiper,
    navigation: {
      prevEl: navigationPrevRef.current,
      nextEl: navigationNextRef.current,
    },
    onBeforeInit: (swiper) => {
      swiper.params.navigation.prevEl = navigationPrevRef.current;
      swiper.params.navigation.nextEl = navigationNextRef.current;
    },
  };

  return (
    <ProfileImageWrapper>
      <NavButton ref={navigationPrevRef} className="prev-button">
        <img src={LEFT_ICON} alt="Previous" />
      </NavButton>
      
      <Swiper {...swiperSettings}>
        {artist.map((img, index) => (
          <SwiperSlide key={index}>
            <img className="swiper-slide-img" src={img} alt={img.alt || `profile-${index + 1}`} />
          </SwiperSlide>
        ))}
      </Swiper>

      <NavButton ref={navigationNextRef} className="next-button">
        <img src={RIGHT_ICON} alt="Next" />
      </NavButton>
    </ProfileImageWrapper>
  );
};

const WorksSection = styled.section`
  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    padding: 0 24px;
  }
`;

const MENU_LIST = ['Movie', 'Drama', 'Play', 'Commercial', 'Show', 'Album'];

const Works = ({ works, menuList, onClickLink }) => {
  // Movie | Drama | Play | Commercial | Show | Album
  // const [selectedMenu, setSelectedMenu] = React.useState(-1);
  const [selectedMenu, setSelectedMenu] = React.useState(0);

  const onChangeMenu = (menu) => setSelectedMenu(menu);

  return (
    <WorksSection>
      <Title>WORKS</Title>
      <WorksMenu selectedMenu={selectedMenu} onChangeMenu={onChangeMenu} menuList={menuList} />
      <WorksList selectedMenu={selectedMenu} works={works} menuList={menuList} onClickLink={onClickLink} />
    </WorksSection>
  );
};

const WorksMenuWrapper = styled.div`
  margin: 40px 0 54px;
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
`;

const WorksMenuButton = styled.span`
  font-family: NanumSquareNeo;
  font-size: 20px;
  font-weight: ${props => props.index === props.selectedMenu ? 'bold' : 'normal'};
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2;
  letter-spacing: -0.38px;
  color: ${props => props.index === props.selectedMenu ? '#7172a5' : '#969696'};
  border-bottom: ${props => props.index === props.selectedMenu ? '2px solid #7172a5' : 'none'};
  cursor: pointer;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    /* flex: 1 1 0; */
  }

  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    font-size: 12px;
  }
`;

const WorksMenu = ({ selectedMenu, onChangeMenu, menuList }) => {
  return (
    <WorksMenuWrapper>
      {menuList.map((menu, index) => (
        <WorksMenuButton selectedMenu={selectedMenu} index={index} key={index} onClick={() => onChangeMenu(index)}>
          {menu}
        </WorksMenuButton>
      ))}
    </WorksMenuWrapper>
  );
};

const WorksListWrapper = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const WorksList = ({ selectedMenu, works, menuList, onClickLink }) => {
  const selectedWorks = works?.[menuList[selectedMenu].toLowerCase()] || [];
  const selectedMenuKey = Object.keys(works)[selectedMenu];

  return (
    <WorksListWrapper
      key={selectedMenu}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
    >
      {selectedWorks.map((work, index) => (
        <WorksListItem 
          menu={selectedMenuKey}
          key={index} 
          work={work}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          onClickLink={onClickLink}
        />
      ))}
    </WorksListWrapper>
  );
};

const WorksListItemWrapper = styled(motion.div)`
  font-family: NanumSquareNeo;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: -0.5px;

  .work-title-wrapper {
    margin: 20px 0 0;
    display: flex;
    align-items: flex-start;
  }

  .work-title {
    color: #505050;
  }

  .work-streaming {
    width: 24px;
    height: 24px;
    margin: 0 0 0 10px;
    cursor: pointer;
  }

  .work-year {
    margin: 0 0 52px;
    color: #969696;
  }

  img {
    width: 100%;
    aspect-ratio: ${props => props.menu === 'album' ? '1/1' : '7/10'};
    object-fit: cover;
    /* cursor: ${props => props.link ? 'pointer' : 'default'}; */
  }

  @media (max-width: ${BREAK_POINTS.TABLET}px) {
    img { max-width: 100%; }
    .work-title { 
      font-size: 14px; 
    }
    .work-year { 
      font-size: 14px; 
      margin: 0 0 40px;
    }
  }

  @media (max-width: ${BREAK_POINTS.MOBILE}px) {
    .work-title-wrapper {
      justify-content: space-between;
    }
    .work-title { 
      font-size: 12px; 
    }
    .work-year { 
      font-size: 12px; 
      margin: 0 0 32px;
    }
  }
`;

const WorksListItem = motion(({ menu, work, onClickLink }) => {
  return (
    <WorksListItemWrapper menu={menu} link={work.link}>
      {work.image ? <img src={work.image} alt={work.title} /> : <NoImage />}
      <div className="work-title-wrapper" onClick={() => onClickLink(work.link)}>
        <div className="work-title">{work.title}</div>
        {work.link && <img className="work-streaming" src={STREAMING_ICON} alt="Streaming" />}
      </div>
      <div className="work-year">{work.year}</div>
    </WorksListItemWrapper>
  );
});

const Title = styled.h2`
  font-family: NanumSquareNeo;
  font-size: 20px;
  font-weight: 700;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.2;
  letter-spacing: -0.38px;
  color: #969696;
`;

const NoImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 7/10;
  background-color: #f0f0f0;

  img {
    width: 71.8px;
    height: 47.8px;
  }
`;

const NoImage = () => {
  return (
    <NoImageWrapper>
      <img src={SYMBOL} alt="KMG symbol" />
    </NoImageWrapper>
  )
}

export default ArtistDetailPresenter;
